import React from 'react';

import { WidgetView, WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';

import { useConfigurationStore } from 'store/configurationsStore';

import { useBaseWidget } from 'hooks/useBaseWidget';

import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import NoData from 'components/common/NoData/noData';
import OverlayWithSpinner from 'components/common/OverlayWithSpinner/overlayWithSpinner';
import { WidgetTitle } from 'components/common/WidgetTitle/WidgetTitle';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';
import widgetWithTitleClasses from 'components/common/WidgetWithTitle/WidgetWithTitle.module.scss';

import { IScoreCardsWrapperProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { ReportingVersionAndPills } from 'components/Dashboards/Reporting/ReportingVersionAndPills/ReportingVersionAndPills';
import { ThreeDotsMenu } from 'components/Dashboards/Reporting/threeDotsMenu/ThreeDotsMenu';
import { CHART_HEIGHT_IN_REPORT_VIEW } from 'components/widgets/ProductivityRiskTrend/ProductivityRiskTrend.constants';
import { useProductivityRiskTrendLogic } from 'components/widgets/ProductivityRiskTrend/ProductivityRiskTrend.logic';
import classes from 'components/widgets/ProductivityRiskTrend/ProductivityRiskTrend.module.scss';
import { useProductivityRiskTrendProvider } from 'components/widgets/ProductivityRiskTrend/ProductivityRiskTrend.provider';
import { CardGraph } from 'components/widgets/ProjectSummary/components/CardsSection/components/CardGraph/CardGraph';
import {
    serializeData,
    serializeOptions,
} from 'components/widgets/ProjectSummary/components/CardsSection/components/RiskCard/RiskCard.utils';

export const ProductivityRiskTrend = ({
    projectId,
    contractId,
    widgetId,
    latestVersionId,
    compareVersionId,
    globalFilterData,
    externalComponents = null,
    setSubTitleForExport,
    localFilters,
    reportWidgetRef = null,
    widgetSelectedProjectAndContract = null,
    widgetViewMode = WidgetView.page,
    title: propsTitle,
    setIsPayloadReady,
}: IScoreCardsWrapperProps) => {
    const { componentKey, routeId: id, endpoints, name: configTitle } = useConfigurationStore().productivityRiskTrend;

    const { ui, api } = useProductivityRiskTrendProvider({
        widgetId,
        latestVersionId,
        localFilters,
        globalFilterData,
        compareVersionId,
        componentKey,
        contractId,
        endpoints,
    });

    const { subTitle, route, editableTitle, setEditableTitle } = useBaseWidget({
        id,
        widgetId,
        projectId,
        contractId,
        latestVersionId,
        title: propsTitle ?? configTitle,
        setSubTitleForExport,
        ui,
        reportWidgetRef,
        apiPayload: api.getProductivityRiskTrend.payload,
        widgetEndpoint: api.getProductivityRiskTrend.endpoint,
    });

    const { logic } = useProductivityRiskTrendLogic({ api, setIsPayloadReady, widgetViewMode });

    return (
        <WidgetWithTitle
                title={
                    <WidgetTitle title={editableTitle} setTitle={setEditableTitle} widgetViewMode={widgetViewMode} />
                }
                className={WidgetViewModeUtils.isReportView(widgetViewMode) ? widgetWithTitleClasses.inReport : ''}
                id={id}
                titleFilters={[
                    <ReportingVersionAndPills
                        key={'ReportingVersionAndPills'}
                        selectedProjectAndContract={widgetSelectedProjectAndContract}
                        pills={ui.pills}
                        widgetViewMode={widgetViewMode}
                    />,
                ]}
                titleComponents={[
                    externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                    !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                        <ExportDropdown key={'export'} title={propsTitle ?? configTitle} subTitle={subTitle} />
                    ),
                    !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                        <ThreeDotsMenu
                            widgetViewMode={widgetViewMode}
                            widgetId={widgetId}
                            componentKey={componentKey}
                            shareTitle={propsTitle ?? configTitle}
                            shareLink={route}
                            projectId={projectId}
                            contractId={contractId}
                        />
                    ),
                ]}
            >
                <div className={classes.widgetCardsWrapper}>
                    {/*Loading State*/}
                    <OverlayWithSpinner isLoading={api.getProductivityRiskTrend.instance.isFetching} />

                    {/*No Data State*/}
                    {!api.getProductivityRiskTrend.instance.isFetching &&
                        !api.getProductivityRiskTrend.instance.data && <NoData />}

                    {/*Show Component State*/}
                    {api.getProductivityRiskTrend.instance.data && (
                        <CardGraph
                            data={api.getProductivityRiskTrend.instance.data}
                            xaxisTitle={'Version'}
                            yaxisTitle={'Risk'}
                            yAxisMaxValue={logic.maxValue}
                            height={CHART_HEIGHT_IN_REPORT_VIEW}
                            className={classes.chart}
                            serializeData={serializeData}
                            serializeOptions={serializeOptions}
                        />
                    )}
                </div>
            </WidgetWithTitle>
    );
};
